import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { DEBUG } from "@/common/config";
import { logger } from "@/util/logger";
import { authStore, snackStore } from "@/stores/Stores";

type JSON = Record<string, null | boolean | number | string>;

const requestInterceptor = () => (request: InternalAxiosRequestConfig<JSON>) => {
  // Set content type if not already set
  if (!request.headers["Content-Type"]) {
    request.headers["Content-Type"] = request.data instanceof FormData ? "multipart/form-data" : "application/json";
  }

  // Tell the server to set HTTP-only cookies
  request.headers["x-set-cookie"] = "true";

  // Include withCredentials to ensure cookies are sent with cross-origin requests
  request.withCredentials = true;

  if (DEBUG) {
    request.headers["X-Domain"] = "auth-local.relux.com";
    logger.debug("Request headers:", request.headers);
  }

  return request;
};

const requestErrorHandler = () => (error: AxiosError) => {
  logger.error("Request error", (error.response?.data as JSON)?.message as string);

  snackStore.showError((error.response?.data as JSON)?.message as string);
  return Promise.reject(error);
};

const responseInterceptor = (response: AxiosResponse<JSON>) => response;

const responseErrorHandler = () => (error: AxiosError) => {
  const status = error.response?.status;

  if (status === 403 || status === 401) {
    if (authStore.user) {
      authStore.refreshToken();
      return;
    }

    // 401 just means nobody is logged in - redirect to login, don't scold them
    if (status !== 401) {
      snackStore.showError((error.response?.data as JSON)?.message as string);
    }

    authStore.logout();
    Promise.reject(error);
    return;
  }

  const except = [422 /* Unprocessable Entity */];
  if (!except.includes(status || 0)) {
    snackStore.showError((error.response?.data as JSON)?.message as string);
  }

  return Promise.reject(error);
};

export const setupInterceptors = () => {
  axios.interceptors.request.use(requestInterceptor(), requestErrorHandler());

  axios.interceptors.response.use(responseInterceptor, responseErrorHandler());
};
